/**
 * Best-effort warm slot for the Foundry inventory.
 *
 * The Foundry surface is presented only once its first view is ready (bounded,
 * FoundrySurface), and the inventory — which carries `firstDiscovered` — is the
 * slowest read in it. Warming it ahead of the press is what lets the press find
 * the list in hand instead of waiting on it behind the tile's busy ring.
 *
 * This is the prefetch half of docs/asset-loading.md, on a real intent signal
 * only: hovering, focusing or touching the Foundry tile, or the Foundry deep
 * link as it opens — never at lobby idle, never a poll, never a blocker. The
 * inventory is every card the player owns, and the owner's rule (26 September
 * 2026) is "it's fine to load things, but not a players entire library": the
 * lobby's idle warm used to read it on every page load, reloads into a match
 * included, and threw it away 20 s later. A surface that takes a warm still in
 * flight waits on it (bounded); a cold, stale or foreign warm falls through to
 * the ordinary request.
 *
 * Account-bound and short-lived on purpose: a warm response is one account's
 * private collection, so it is keyed by uid AND language, expires quickly, and
 * is consumed once.
 *
 * The fusion prices ride here too (`warmConfig`). They end the canvas hint
 * ("Fuse two elements from the list · 50 ✦ / discovery"), which used to open
 * on "Loading fusion prices..." and swap to the figure ~400 ms later, sliding
 * the rest of the sentence sideways. Unlike the inventory they are public, one
 * small read, and change only with a deploy, so the lobby warms them at idle
 * (Lobby.warmAtIdle) as well as on intent; they are remembered for a minute
 * and shared rather than consumed: the surface prints them on its first frame.
 */

import { onStardustAnnounced } from './stardust-chip.js';

// Declared from the IIFE itself so consumers see its real shape; an `= {}`
// placeholder typed every call through it as a property of `{}`.
const FoundryInventoryWarm = (function () {
    // Long enough to cover an intent-to-click gap, short enough that a stale
    // collection can never be shown after a craft, purchase or pack opening
    // (with the announced-Stardust drop below for what the window can hold).
    const WARM_TTL_MS = 20000;
    // A prefetch is an optimisation; it may never outlive the request it saves.
    const WARM_REQUEST_TIMEOUT_MS = 8000;

    let warm = null; // { key, at, promise }

    function keyFor(uid, lang) {
        return `${String(uid || '')}::${String(lang || 'en')}`;
    }

    function fresh(entry) {
        return Boolean(entry) && (Date.now() - entry.at) < WARM_TTL_MS;
    }

    /**
     * Start (or reuse) a warm request. Never throws and never rejects into the
     * caller: a failed warm simply leaves the slot empty.
     */
    function warmUp(user, lang) {
        try {
            const uid = user?.uid;
            if (!uid || typeof user.getIdToken !== 'function') return;
            const key = keyFor(uid, lang);
            if (fresh(warm) && warm.key === key) return;
            const at = Date.now();
            const promise = (async () => {
                /* BOUNDED, like every other Foundry read. A warm that never
                   settles is worse than no warm at all: the surface awaits the
                   one it takes, so a stalled prefetch means the inventory never
                   arrives and nothing reports an error — reported from an
                   iPhone as "Moon Fuse was not loading anymore", cured by a tab
                   switch, which takes no warm and issues the ordinary request. */
                const controller = new AbortController();
                const deadline = setTimeout(() => controller.abort(), WARM_REQUEST_TIMEOUT_MS);
                try {
                    const token = await user.getIdToken();
                    const response = await fetch(
                        `/api/foundry/inventory?lang=${encodeURIComponent(lang || 'en')}`,
                        { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal },
                    );
                    if (!response.ok) throw new Error(`inventory warm ${response.status}`);
                    return await response.json();
                } finally {
                    clearTimeout(deadline);
                }
            })();
            // Attach a catch NOW so a failed warm can never surface as an
            // unhandled rejection; `take()` re-reads the same settled promise.
            promise.catch(() => {});
            warm = { key, at, promise };
        } catch (_) { /* warming is best effort, always */ }
    }

    /**
     * Consume the warm response if it belongs to this account and language and
     * has not gone stale. Returns null otherwise — the caller then fetches
     * exactly as it did before this module existed.
     */
    function take(uid, lang) {
        const entry = warm;
        if (!fresh(entry) || entry.key !== keyFor(uid, lang)) return null;
        warm = null;
        return entry.promise;
    }

    /** Drop any warm response — used when the signed-in account changes. */
    function clear() {
        warm = null;
    }

    /* A MOVED COLLECTION IS NEVER SERVED WARM. A hover over the tile is not a
       press: the 20 s window can still hold a pack opening, a purchase or a
       trade before the Foundry opens. Every surface that spends or grants
       Stardust announces it on this event (stardust-chip.js), and an
       announcement means the collection may have changed: the next open reads
       it afresh. */
    onStardustAnnounced(() => clear());

    // Same lifetime as the Moon Exchange's config memo: a deploy that moves a
    // price is visible within a minute, and a press never waits on a re-read.
    const CONFIG_FRESH_MS = 60000;
    let config = null; // { at, costs }
    let configRead = null; // Promise<costs | null> while a read is in flight
    /* THE WHOLE PRICE TABLE, as the server sent it. The Collection prices
       Stellar crafts too, so it used to read the table itself — a second
       request beside this warm's on every boot (measured 25 September 2026).
       Only a read of the payload sets it: a surface remembering its own two
       prices must not pass for the whole table. */
    let table = null; // { at, cost }

    function normalizeCosts(cost) {
        const crafting = Number(cost?.CRAFTING);
        const discovery = Number(cost?.DISCOVERY);
        if (
            !Number.isSafeInteger(crafting) || crafting <= 0
            || !Number.isSafeInteger(discovery) || discovery <= 0
        ) return null;
        return { CRAFTING: crafting, DISCOVERY: discovery };
    }

    /** Remember prices the surface read itself, so the next open prints them at once. */
    function rememberConfig(cost) {
        const costs = normalizeCosts(cost);
        if (costs) config = { at: Date.now(), costs };
    }

    /** The remembered prices while they are fresh, else null. Synchronous: first-frame use. */
    function peekConfig() {
        if (!config || (Date.now() - config.at) >= CONFIG_FRESH_MS) return null;
        return { ...config.costs };
    }

    /** The read in flight, if any; it resolves to the prices or null and never rejects. */
    function pendingConfig() {
        return configRead;
    }

    function keepTable(cost) {
        if (!cost || typeof cost !== 'object') return;
        const entries = Object.entries(cost)
            .filter(([, value]) => Number.isFinite(Number(value)))
            .map(([key, value]) => [key, Number(value)]);
        if (entries.length) table = { at: Date.now(), cost: Object.fromEntries(entries) };
    }

    function peekTable() {
        if (!table || (Date.now() - table.at) >= CONFIG_FRESH_MS) return null;
        return { ...table.cost };
    }

    /** Read the prices ahead of the press. Never throws, never rejects, one read at a time. */
    function warmConfig() {
        if (peekConfig() || configRead) return;
        readConfig();
    }

    /**
     * Every price the server lists (Stellar included), read once for every
     * surface that needs it: fresh from memory, joined to the read already on
     * the wire, or read now. Resolves to null when the read fails; never rejects.
     */
    function costTable() {
        const kept = peekTable();
        if (kept) return Promise.resolve(kept);
        const read = readConfig();
        return read ? read.then(() => peekTable()) : Promise.resolve(null);
    }

    function readConfig() {
        if (configRead) return configRead;
        try {
            const controller = new AbortController();
            const deadline = setTimeout(() => controller.abort(), WARM_REQUEST_TIMEOUT_MS);
            const read = Promise.resolve()
                .then(() => fetch('/api/foundry/config', { signal: controller.signal }))
                .then((response) => (response.ok ? response.json() : null))
                .then((payload) => {
                    rememberConfig(payload?.cost);
                    keepTable(payload?.cost);
                    return peekConfig();
                })
                .catch(() => null)
                .finally(() => {
                    clearTimeout(deadline);
                    if (configRead === read) configRead = null;
                });
            configRead = read;
            return read;
        } catch (_) { /* warming is best effort, always */ }
        return null;
    }

    return {
        warmUp,
        take,
        clear,
        warmConfig,
        costTable,
        peekConfig,
        pendingConfig,
        rememberConfig,
        WARM_TTL_MS,
        WARM_REQUEST_TIMEOUT_MS,
        CONFIG_FRESH_MS,
    };
})();

export { FoundryInventoryWarm };
